/**
 * MomentumInsightCard Component
 *
 * Displays a growth/momentum stock with price action indicators.
 * Shows moving average comparison and distance from 52-week high.
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { InsightConfig } from '../quizTypes';
import type { FMPQuote } from '../../../services/fmpDataService';
import { typography } from '../../../common/styles/universalTheme';
import InsightCard from './InsightCard';

interface MomentumInsightCardProps {
  config: InsightConfig;
  quote: FMPQuote | null;
  isLoading: boolean;
  onNavigate: (route: string) => void;
}

const MomentumInsightCard: React.FC<MomentumInsightCardProps> = ({
  config,
  quote,
  isLoading,
  onNavigate,
}) => {
  const theme = useTheme();

  // Calculate distance from 52-week high
  const distanceFromHigh =
    quote && quote.yearHigh > 0
      ? ((quote.price - quote.yearHigh) / quote.yearHigh) * 100
      : null;

  // Check if price is above moving averages
  const above50MA = quote && quote.priceAvg50 > 0 && quote.price > quote.priceAvg50;
  const above200MA = quote && quote.priceAvg200 > 0 && quote.price > quote.priceAvg200;

  // Determine trend indicator
  const getTrendIndicator = (): { text: string; color: string } => {
    if (above50MA && above200MA) {
      return { text: 'Strong uptrend', color: theme.palette.success.main };
    }
    if (above50MA) {
      return { text: 'Above 50-day avg', color: theme.palette.success.main };
    }
    if (above200MA) {
      return { text: 'Above 200-day avg', color: theme.palette.text.secondary };
    }
    return { text: 'Consolidating', color: theme.palette.text.secondary };
  };

  const trend = getTrendIndicator();

  // Determine change color
  const changeColor =
    quote && quote.changesPercentage >= 0
      ? theme.palette.success.main
      : theme.palette.error.main;

  return (
    <InsightCard
      label={config.label}
      rationale={config.rationale}
      deepLinkRoute={config.deepLinkRoute}
      onNavigate={onNavigate}
      isLoading={isLoading}
    >
      {quote && (
        <Box>
          {/* Symbol and company name */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {quote.symbol}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {quote.name}
            </Typography>
          </Box>

          {/* Price and change */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.75 }}>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontVariantNumeric: 'tabular-nums lining-nums',
                fontWeight: 600,
                fontSize: typography.fontSize.lg,
                color: 'text.primary',
              }}
            >
              ${quote.price.toFixed(2)}
            </Typography>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontVariantNumeric: 'tabular-nums lining-nums',
                fontSize: typography.fontSize.sm,
                fontWeight: 500,
                color: changeColor,
              }}
            >
              {quote.changesPercentage >= 0 ? '+' : ''}
              {quote.changesPercentage.toFixed(2)}%
            </Typography>
          </Box>

          {/* Momentum indicators */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Trend indicator */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                Trend
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: trend.color,
                  fontWeight: 500,
                }}
              >
                {trend.text}
              </Typography>
            </Box>

            {/* Distance from 52-week high */}
            {distanceFromHigh !== null && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                  From 52W High
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontFamily: typography.fontFamily.mono,
                    fontVariantNumeric: 'tabular-nums',
                    color: distanceFromHigh >= -5 ? theme.palette.success.main : 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {distanceFromHigh.toFixed(1)}%
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </InsightCard>
  );
};

export default MomentumInsightCard;
